import React from 'react'
import {
    PencilSquareIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import type { ToolQuestionEntry } from '../toolQuestions'

type QuestionsPanelProps = {
    questions: ToolQuestionEntry[]
    onCreate: () => void
    onEdit: (clientKey: string) => void
    onDelete: (clientKey: string) => void
    chipImageSrc: (chipImage: string) => string | null
}

const QuestionsPanel: React.FC<QuestionsPanelProps> = ({
    questions,
    onCreate,
    onEdit,
    onDelete,
    chipImageSrc,
}) => {
    return (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm h-[45vh] flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3 shrink-0">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Questions
                </h4>

                <button
                    type="button"
                    onClick={onCreate}
                    className="inline-flex items-center rounded-md border border-blue-500 bg-transparent px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:hover:bg-blue-900/20"
                >
                    Create Question
                </button>
            </div>

            {questions.length > 0 ? (
                <div className="flex-1 overflow-y-auto pr-1">
                    <ul className="space-y-1">
                        {questions.map((q) => {
                            const thumb = chipImageSrc(q.chipImage)
                            return (
                                <li
                                    key={q.clientKey}
                                    className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50/80 px-2 py-1.5 dark:border-slate-600 dark:bg-slate-900/40"
                                >
                                    <div className="h-8 w-8 shrink-0 overflow-hidden rounded border border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-800">
                                        {thumb ? (
                                            <img
                                                src={thumb}
                                                alt=""
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div
                                                className="flex h-full w-full items-center justify-center text-[9px] text-gray-400 dark:text-gray-500"
                                                aria-hidden
                                            >
                                                —
                                            </div>
                                        )}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">
                                            {q.question.trim() || '—'}
                                        </p>
                                        <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">
                                            {q.options.length} option
                                            {q.options.length === 1 ? '' : 's'}
                                        </p>
                                    </div>

                                    <span
                                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${q.isMultiSelect === 1
                                            ? 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200'
                                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                            }`}
                                    >
                                        {q.isMultiSelect === 1
                                            ? 'Multi Select'
                                            : 'Single Select'}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={() => onEdit(q.clientKey)}
                                        className="rounded p-1 text-gray-600 hover:bg-white hover:text-gray-900 dark:text-gray-300 dark:hover:bg-slate-800 dark:hover:text-white"
                                        aria-label="Edit question"
                                    >
                                        <PencilSquareIcon className="h-4 w-4" />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => onDelete(q.clientKey)}
                                        className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-100"
                                        aria-label="Delete question"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-gray-300 py-6 text-sm text-gray-500 dark:border-slate-600 dark:text-gray-400">
                    No questions added yet. Click “Create Questions” to get started.
                </div>
            )}
        </div>
    )
}

export default QuestionsPanel

